import React, { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api, errMsg } from "../lib/api";
import { Plus, Trash2, ShieldAlert, LibraryBig, Upload, X, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";

const LEVELS = ["Low", "Medium", "High"];
const CSV_SAMPLE = `name,description,likelihood,impact,mitigation,role,program
Vendor delay,Primary vendor delivers late,High,High,Keep a backup vendor warm,,
Scope creep,Stakeholder adds mid-sprint asks,High,Medium,Weekly scope review with sponsor,Project Manager,
Resource burnout,Team runs over 160h/mo,Medium,High,Rebalance via Resource Load view,,`;

export default function RiskTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [roles, setRoles] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [t, r, p] = await Promise.all([
        api.get("/risk-templates"),
        api.get("/roles"),
        api.get("/programs"),
      ]);
      setTemplates(t.data || []);
      setRoles(r.data || []);
      setPrograms(p.data || []);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const seedStarter = async () => {
    if (!window.confirm("Seed your library with 10 industry-standard risk templates?")) return;
    try {
      const { data } = await api.post("/risk-templates/seed-starter");
      toast.success(`Seeded ${data.created_count} starter risk templates`);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const add = async () => {
    try {
      const { data } = await api.post("/risk-templates", { data: { name: "New risk template", description: "", likelihood: "Medium", impact: "Medium" } });
      setTemplates((x) => [data, ...x]);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const update = async (id, patch) => {
    setTemplates((xs) => xs.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    try { await api.put(`/risk-templates/${id}`, { data: patch }); }
    catch (e) { toast.error(errMsg(e)); load(); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this risk template?")) return;
    try { await api.delete(`/risk-templates/${id}`); setTemplates((xs) => xs.filter((t) => t.id !== id)); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const importCsv = async ({ dryRun }) => {
    if (!csvText.trim()) { toast.error("Paste some CSV first"); return; }
    setImporting(true); setImportResult(null);
    try {
      const { data } = await api.post("/risk-templates/import", { data: { csv: csvText, dry_run: !!dryRun } });
      setImportResult(data);
      if (!dryRun) {
        toast.success(`Imported ${data.created_count} risk template${data.created_count === 1 ? "" : "s"}`);
        setCsvText("");
        await load();
      }
    } catch (e) { toast.error(errMsg(e)); }
    setImporting(false);
  };

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-white inline-flex items-center gap-3" style={{ fontFamily: "Outfit" }}>
            <LibraryBig className="text-rose-300" size={28} /> Risk Library
          </h1>
          <p className="text-slate-400 mt-1 max-w-2xl">
            Save the risks you keep hitting on every project — once. Pin each one to a Role or a Program and new projects can auto-seed their Risk Register from the matching templates.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setImportOpen(true); setImportResult(null); }} data-testid="open-csv-import-btn" className="border border-slate-700 hover:border-rose-500 text-slate-200 px-3 py-2 rounded-lg inline-flex items-center gap-2 text-sm">
            <Upload size={14} /> Import CSV
          </button>
          <button onClick={add} data-testid="add-risk-template-btn" className="bg-gradient-to-r from-rose-500 to-rose-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2 shadow-lg shadow-rose-900/30">
            <Plus size={14} /> New template
          </button>
        </div>
      </div>

      {importOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="csv-import-modal">
          <div className="pc-card w-full max-w-3xl mt-16">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <FileText className="text-rose-300" size={16}/>
                <div>
                  <div className="text-sm font-semibold text-white" style={{ fontFamily: "Outfit" }}>Import risk templates from CSV</div>
                  <div className="text-xs text-slate-400">Paste a CSV with these columns (any order, case-insensitive): <code className="text-rose-300">name, description, likelihood, impact, mitigation, role, program</code>. Role/Program match by name — leave blank for "any".</div>
                </div>
              </div>
              <button onClick={() => setImportOpen(false)} data-testid="close-csv-modal-btn" className="text-slate-500 hover:text-white"><X size={16}/></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-[10px] uppercase tracking-widest text-slate-400">CSV content</label>
                <button onClick={() => setCsvText(CSV_SAMPLE)} data-testid="load-csv-sample-btn" className="text-[11px] text-blue-400 hover:text-blue-300">Load sample</button>
              </div>
              <textarea
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                rows={10}
                placeholder={CSV_SAMPLE}
                data-testid="csv-textarea"
                className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:border-rose-500 text-sm font-mono bg-slate-950/60 text-slate-200"
              />
              {importResult && (
                <div className="text-xs space-y-1" data-testid="csv-import-result">
                  <div className="text-slate-300">
                    {importResult.dry_run ? "Preview:" : "Imported:"} <strong className="text-emerald-300">{importResult.created_count}</strong> template{importResult.created_count === 1 ? "" : "s"}
                    {importResult.skipped?.length ? <span className="text-amber-300"> · {importResult.skipped.length} warning{importResult.skipped.length === 1 ? "" : "s"}</span> : null}
                  </div>
                  {importResult.skipped?.length > 0 && (
                    <ul className="text-amber-300/80 list-disc ml-5 max-h-24 overflow-y-auto">
                      {importResult.skipped.map((s) => <li key={`${s.row}-${s.reason}`}>Row {s.row}: {s.reason}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-800">
              <button onClick={() => setImportOpen(false)} className="text-sm text-slate-400 hover:text-white px-3 py-2">Cancel</button>
              <button onClick={() => importCsv({ dryRun: true })} disabled={importing} data-testid="csv-dry-run-btn" className="border border-slate-700 hover:border-blue-500 text-slate-200 px-3 py-2 rounded-lg text-sm">Preview</button>
              <button onClick={() => importCsv({ dryRun: false })} disabled={importing} data-testid="csv-import-submit-btn"
                className="bg-gradient-to-r from-rose-500 to-rose-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                {importing ? "Importing…" : "Import to library"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="pc-card overflow-x-auto" data-testid="risk-templates-table">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-widest text-blue-300/80 border-b border-slate-800">
            <tr>
              <th className="p-3">Label</th>
              <th className="p-3">Description</th>
              <th className="p-3 w-28">Likelihood</th>
              <th className="p-3 w-28">Impact</th>
              <th className="p-3">Mitigation</th>
              <th className="p-3 w-40">Pin to role</th>
              <th className="p-3 w-40">Pin to program</th>
              <th className="p-3 w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {loading && <tr><td colSpan={8} className="p-8 text-center text-slate-400">Loading library…</td></tr>}
            {!loading && templates.length === 0 && (
              <tr>
                <td colSpan={8} className="p-10 text-center text-slate-400" data-testid="risk-templates-empty">
                  <ShieldAlert className="inline-block mb-2 text-rose-400" />
                  <div className="mb-3">No risk templates yet.</div>
                  <button
                    onClick={seedStarter}
                    data-testid="seed-starter-btn"
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-rose-500 to-rose-700 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-lg shadow-rose-900/30"
                  >
                    <Sparkles size={14} /> Seed with industry-standard risks
                  </button>
                  <div className="text-xs text-slate-500 mt-2">10 proven project-management risks (scope creep, vendor delay, resource burnout, etc.)</div>
                </td>
              </tr>
            )}
            {!loading && templates.map((t) => (
              <tr key={t.id} data-testid={`risk-template-row-${t.id}`}>
                <td className="p-2"><Inp value={t.name || ""} onChange={(v) => update(t.id, { name: v })} testid={`tpl-name-${t.id}`} /></td>
                <td className="p-2"><Inp value={t.description || ""} onChange={(v) => update(t.id, { description: v })} placeholder="e.g. Vendor contract may slip" testid={`tpl-desc-${t.id}`} /></td>
                <td className="p-2"><Sel value={t.likelihood || "Medium"} onChange={(v) => update(t.id, { likelihood: v })} options={LEVELS} testid={`tpl-likelihood-${t.id}`} /></td>
                <td className="p-2"><Sel value={t.impact || "Medium"} onChange={(v) => update(t.id, { impact: v })} options={LEVELS} testid={`tpl-impact-${t.id}`} /></td>
                <td className="p-2"><Inp value={t.mitigation || ""} onChange={(v) => update(t.id, { mitigation: v })} placeholder="Mitigation plan" testid={`tpl-mitigation-${t.id}`} /></td>
                <td className="p-2">
                  <Sel value={t.role_id || ""} onChange={(v) => update(t.id, { role_id: v || null })} options={[["", "— Any —"], ...roles.map((r) => [r.id, r.name])]} testid={`tpl-role-${t.id}`} />
                </td>
                <td className="p-2">
                  <Sel value={t.program_id || ""} onChange={(v) => update(t.id, { program_id: v || null })} options={[["", "— Any —"], ...programs.map((p) => [p.id, p.name])]} testid={`tpl-program-${t.id}`} />
                </td>
                <td className="p-2 text-right">
                  <button onClick={() => remove(t.id)} data-testid={`delete-risk-template-${t.id}`} className="text-slate-500 hover:text-rose-400"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500 mt-4">
        Tip: open any project's <strong>Risks</strong> tab and click <em>Load from library</em> to auto-seed matching templates based on the project's program and roles.
      </p>
    </AppLayout>
  );
}

function Inp({ value, onChange, placeholder = "", testid }) {
  return (
    <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
      className="w-full bg-transparent border border-transparent hover:border-slate-700 focus:border-blue-500 rounded px-2 py-1 text-sm outline-none text-slate-200 placeholder:text-slate-500" />
  );
}

function Sel({ value, onChange, options, testid }) {
  const opts = options.map((o) => (Array.isArray(o) ? o : [o, o]));
  return (
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full bg-transparent border border-transparent hover:border-slate-700 focus:border-blue-500 rounded px-2 py-1 text-sm outline-none text-slate-200">
      {opts.map(([v, l]) => <option key={v} value={v} className="bg-slate-900 text-white">{l}</option>)}
    </select>
  );
}
